import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { workoutRecordsService } from '../../../services/workoutRecordsService';
import { workoutService } from '../../../services/trackingService';
import WorkoutRoutinesGuide from './WorkoutRoutinesGuide';

const WORKOUT_TYPES = [
  { value: 'leg_day', label: 'Leg Day', icon: 'Dumbbell', color: 'text-orange-500', bg: 'bg-orange-500/10' },
  { value: 'push', label: 'Push', icon: 'ArrowUp', color: 'text-blue-500', bg: 'bg-blue-500/10' },
  { value: 'pull', label: 'Pull', icon: 'ArrowDown', color: 'text-purple-500', bg: 'bg-purple-500/10' },
  { value: 'upper_body', label: 'Upper Body', icon: 'Activity', color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
  { value: 'lower_body', label: 'Lower Body', icon: 'TrendingDown', color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
  { value: 'full_body', label: 'Full Body', icon: 'Zap', color: 'text-green-500', bg: 'bg-green-500/10' },
  { value: 'cardio', label: 'Cardio', icon: 'Heart', color: 'text-red-500', bg: 'bg-red-500/10' },
  { value: 'hiit', label: 'HIIT', icon: 'Flame', color: 'text-rose-500', bg: 'bg-rose-500/10' },
  { value: 'yoga', label: 'Yoga', icon: 'Leaf', color: 'text-teal-500', bg: 'bg-teal-500/10' },
  { value: 'custom', label: 'Custom', icon: 'Edit3', color: 'text-muted-foreground', bg: 'bg-muted/30' },
];

const formatTime = (ms) => {
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${String(h)?.padStart(2, '0')}:${String(m)?.padStart(2, '0')}:${String(s)?.padStart(2, '0')}`;
  return `${String(m)?.padStart(2, '0')}:${String(s)?.padStart(2, '0')}`;
};

const formatMinutes = (ms) => Math.max(1, Math.round(ms / 60000));

const WorkoutStopwatch = ({ userId, onSessionComplete }) => {
  const [phase, setPhase] = useState('idle'); // idle | selecting | running | paused | done
  const [selectedType, setSelectedType] = useState(null);
  const [customName, setCustomName] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [pausedAt, setPausedAt] = useState(0);
  const [bestRoutine, setBestRoutine] = useState(null);
  const [loadingBest, setLoadingBest] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showRoutinesGuide, setShowRoutinesGuide] = useState(false);
  const intervalRef = useRef(null);

  // Tick
  useEffect(() => {
    if (phase === 'running') {
      intervalRef.current = setInterval(() => {
        setElapsed(Date.now() - startTime + pausedAt);
      }, 100);
    } else {
      clearInterval(intervalRef?.current);
    }
    return () => clearInterval(intervalRef?.current);
  }, [phase, startTime, pausedAt]);

  // Load best routine when type is selected
  const loadBestRoutine = useCallback(async (typeValue) => {
    if (!userId) return;
    setLoadingBest(true);
    try {
      const records = await workoutRecordsService?.getWorkoutRecords(userId);
      // Filter records matching this workout type (stored in notes or exercise_name)
      const typeLabel = WORKOUT_TYPES?.find(t => t?.value === typeValue)?.label || typeValue;
      const relevant = records?.filter(r =>
        r?.notes?.toLowerCase()?.includes(typeValue) ||
        r?.notes?.toLowerCase()?.includes(typeLabel?.toLowerCase()) ||
        r?.exercise_name?.toLowerCase()?.includes(typeValue) ||
        r?.exercise_name?.toLowerCase()?.includes(typeLabel?.toLowerCase())
      );

      if (relevant?.length === 0) {
        setBestRoutine(null);
        return;
      }

      // Best session = highest total volume (weight × reps × sets) or longest duration
      let best = null;
      let bestScore = -1;
      for (const r of relevant) {
        const volume = (r?.weight || 0) * (r?.reps || 1) * (r?.sets || 1);
        const score = volume > 0 ? volume : (r?.duration_minutes || 0) * 10;
        if (score > bestScore) {
          bestScore = score;
          best = r;
        }
      }

      // PRs for this type
      const prs = {};
      for (const r of relevant) {
        if (r?.weight > 0 && (!prs?.weight || r?.weight > prs?.weight?.value)) prs.weight = { value: r?.weight, date: r?.workout_date };
        if (r?.reps > 0 && (!prs?.reps || r?.reps > prs?.reps?.value)) prs.reps = { value: r?.reps, date: r?.workout_date };
        if (r?.duration_minutes > 0 && (!prs?.duration || r?.duration_minutes > prs?.duration?.value)) prs.duration = { value: r?.duration_minutes, date: r?.workout_date };
        if (r?.calories > 0 && (!prs?.calories || r?.calories > prs?.calories?.value)) prs.calories = { value: r?.calories, date: r?.workout_date };
      }

      setBestRoutine({ best, prs, totalSessions: relevant?.length });
    } catch (err) {
      console.error('Error loading best routine:', err);
      setBestRoutine(null);
    } finally {
      setLoadingBest(false);
    }
  }, [userId]);

  const handleSelectType = (type) => {
    setSelectedType(type);
    if (type?.value !== 'custom') setCustomName('');
    loadBestRoutine(type?.value);
  };

  const handleStart = () => {
    setStartTime(Date.now());
    setPausedAt(0);
    setElapsed(0);
    setPhase('running');
    setSaveSuccess(false);
  };

  const handlePause = () => {
    setPausedAt(elapsed);
    setPhase('paused');
  };

  const handleResume = () => {
    setStartTime(Date.now());
    setPhase('running');
  };

  const handleStop = () => {
    setPhase('done');
  };

  const handleReset = () => {
    setPhase('idle');
    setSelectedType(null);
    setCustomName('');
    setElapsed(0);
    setPausedAt(0);
    setStartTime(null);
    setBestRoutine(null);
    setSaveSuccess(false);
  };

  const handleSaveSession = async () => {
    if (!userId || !selectedType) return;
    setSaving(true);
    try {
      const durationMinutes = formatMinutes(elapsed);
      const workoutLabel = selectedType?.value === 'custom' && customName
        ? customName
        : selectedType?.label;

      // Save to workout_records
      await workoutRecordsService?.logWorkoutRecord(userId, {
        exerciseName: workoutLabel,
        durationMinutes,
        notes: `${selectedType?.value} | Stopwatch session`,
        date: new Date()?.toISOString()?.split('T')?.[0],
      });

      // Save to activity logs (workout_logs)
      await workoutService?.createWorkout(userId, {
        name: workoutLabel,
        type: selectedType?.value === 'leg_day' || selectedType?.value === 'lower_body' ? 'strength' :
              selectedType?.value === 'cardio' || selectedType?.value === 'hiit' ? 'cardio' :
              selectedType?.value === 'yoga' ? 'yoga' : 'strength',
        duration: durationMinutes,
        calories: Math.round(durationMinutes * (
          selectedType?.value === 'hiit' ? 12 :
          selectedType?.value === 'cardio' ? 10 :
          selectedType?.value === 'yoga' ? 3 : 8
        )),
        intensity: selectedType?.value === 'hiit' ? 'high' : selectedType?.value === 'yoga' ? 'low' : 'moderate',
        notes: `Stopwatch session — ${workoutLabel}`,
        date: new Date()?.toISOString()?.split('T')?.[0],
      });

      setSaveSuccess(true);
      // Notify parent to auto-populate form and refresh
      onSessionComplete?.({
        duration: durationMinutes,
        type: selectedType?.value === 'leg_day' || selectedType?.value === 'lower_body' ? 'strength' :
              selectedType?.value === 'cardio' || selectedType?.value === 'hiit' ? 'cardio' :
              selectedType?.value === 'yoga' ? 'yoga' : 'strength',
        name: workoutLabel,
        calories: Math.round(durationMinutes * (
          selectedType?.value === 'hiit' ? 12 :
          selectedType?.value === 'cardio' ? 10 :
          selectedType?.value === 'yoga' ? 3 : 8
        )),
      });
    } catch (err) {
      console.error('Error saving session:', err);
      alert('Error saving session. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const selectedTypeInfo = WORKOUT_TYPES?.find(t => t?.value === selectedType?.value);

  return (
    <motion.div
      className="bg-card rounded-xl border border-border/40 shadow-wellness overflow-hidden"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border/40">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 bg-primary/10 rounded-lg">
            <Icon name="Timer" size={18} className="text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-sm">Workout Stopwatch</h3>
            <p className="text-xs text-muted-foreground">Time your session live</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowRoutinesGuide(!showRoutinesGuide)}
            className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${
              showRoutinesGuide
                ? 'bg-primary/10 border-primary/30 text-primary' :'border-border/40 text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            <Icon name="BookOpen" size={13} />
            Routines
          </button>
          {phase !== 'idle' && (
            <button onClick={handleReset} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
              <Icon name="RotateCcw" size={13} />
              Reset
            </button>
          )}
        </div>
      </div>
      <div className="p-6 space-y-5">
        {/* Routines Guide Panel (inline toggle) */}
        <AnimatePresence>
          {showRoutinesGuide && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <WorkoutRoutinesGuide
                selectedType={selectedType?.value}
                onClose={() => setShowRoutinesGuide(false)}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* IDLE: Workout type selector */}
        {phase === 'idle' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <p className="text-sm font-medium text-foreground">Select workout type to begin</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {WORKOUT_TYPES?.map(type => (
                <button
                  key={type?.value}
                  onClick={() => handleSelectType(type)}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-all duration-200 text-center ${
                    selectedType?.value === type?.value
                      ? `border-primary ${type?.bg} ring-1 ring-primary/40`
                      : 'border-border/40 hover:border-border hover:bg-muted/30'
                  }`}
                >
                  <Icon name={type?.icon} size={16} className={selectedType?.value === type?.value ? type?.color : 'text-muted-foreground'} />
                  <span className={`text-xs font-medium leading-tight ${selectedType?.value === type?.value ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {type?.label}
                  </span>
                </button>
              ))}
            </div>

            {/* Custom name input */}
            {selectedType?.value === 'custom' && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
                <input
                  type="text"
                  placeholder="Enter custom workout name..."
                  value={customName}
                  onChange={e => setCustomName(e?.target?.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </motion.div>
            )}

            {/* Best Routine Panel */}
            {selectedType && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-lg border border-border/40 bg-muted/20 p-4 space-y-3"
              >
                <div className="flex items-center gap-2">
                  <Icon name="Trophy" size={14} className="text-yellow-500" />
                  <span className="text-xs font-semibold text-foreground">Best Routine — {selectedType?.label}</span>
                </div>
                {loadingBest ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <div className="w-3 h-3 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                    Analyzing past sessions...
                  </div>
                ) : bestRoutine ? (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">{bestRoutine?.totalSessions} session{bestRoutine?.totalSessions !== 1 ? 's' : ''} logged</p>
                    <div className="grid grid-cols-2 gap-2">
                      {bestRoutine?.prs?.weight && (
                        <div className="bg-card rounded-md p-2 border border-border/30">
                          <p className="text-xs text-muted-foreground">Best Weight</p>
                          <p className="text-sm font-bold text-foreground">{bestRoutine?.prs?.weight?.value} kg</p>
                          <p className="text-xs text-muted-foreground">{bestRoutine?.prs?.weight?.date}</p>
                        </div>
                      )}
                      {bestRoutine?.prs?.reps && (
                        <div className="bg-card rounded-md p-2 border border-border/30">
                          <p className="text-xs text-muted-foreground">Best Reps</p>
                          <p className="text-sm font-bold text-foreground">{bestRoutine?.prs?.reps?.value} reps</p>
                          <p className="text-xs text-muted-foreground">{bestRoutine?.prs?.reps?.date}</p>
                        </div>
                      )}
                      {bestRoutine?.prs?.duration && (
                        <div className="bg-card rounded-md p-2 border border-border/30">
                          <p className="text-xs text-muted-foreground">Best Duration</p>
                          <p className="text-sm font-bold text-foreground">{bestRoutine?.prs?.duration?.value} min</p>
                          <p className="text-xs text-muted-foreground">{bestRoutine?.prs?.duration?.date}</p>
                        </div>
                      )}
                      {bestRoutine?.prs?.calories && (
                        <div className="bg-card rounded-md p-2 border border-border/30">
                          <p className="text-xs text-muted-foreground">Best Calories</p>
                          <p className="text-sm font-bold text-foreground">{bestRoutine?.prs?.calories?.value} kcal</p>
                          <p className="text-xs text-muted-foreground">{bestRoutine?.prs?.calories?.date}</p>
                        </div>
                      )}
                    </div>
                    {bestRoutine?.best && (
                      <div className="flex items-start gap-2 bg-primary/5 rounded-md p-2 border border-primary/20">
                        <Icon name="Star" size={12} className="text-primary mt-0.5 shrink-0" />
                        <p className="text-xs text-foreground">
                          Best session on <span className="font-medium">{bestRoutine?.best?.workout_date}</span>
                          {bestRoutine?.best?.weight > 0 && ` — ${bestRoutine?.best?.weight}kg × ${bestRoutine?.best?.reps} reps × ${bestRoutine?.best?.sets} sets`}
                          {bestRoutine?.best?.duration_minutes > 0 && ` — ${bestRoutine?.best?.duration_minutes} min`}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No past sessions found for this workout type. Start your first one!</p>
                )}
              </motion.div>
            )}

            <Button
              variant="default"
              onClick={handleStart}
              disabled={!selectedType || (selectedType?.value === 'custom' && !customName?.trim())}
              iconName="Play"
              iconPosition="left"
              className="w-full"
            >
              Start Timer
            </Button>
          </motion.div>
        )}

        {/* RUNNING / PAUSED / DONE: Timer display */}
        {(phase === 'running' || phase === 'paused' || phase === 'done') && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
            {/* Workout type badge */}
            {selectedTypeInfo && (
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${selectedTypeInfo?.bg} border border-border/30`}>
                <Icon name={selectedTypeInfo?.icon} size={13} className={selectedTypeInfo?.color} />
                <span className="text-xs font-medium text-foreground">
                  {selectedType?.value === 'custom' && customName ? customName : selectedTypeInfo?.label}
                </span>
                {phase === 'running' && (
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                    <span className="text-xs text-green-600 dark:text-green-400">Live</span>
                  </span>
                )}
                {phase === 'paused' && <span className="text-xs text-yellow-600 dark:text-yellow-400">Paused</span>}
                {phase === 'done' && <span className="text-xs text-primary">Done</span>}
              </div>
            )}

            {/* Timer display */}
            <div className="flex flex-col items-center py-4">
              <motion.div
                key={phase}
                className={`text-5xl font-mono font-bold tabular-nums tracking-tight ${
                  phase === 'running' ? 'text-foreground' :
                  phase === 'paused' ? 'text-yellow-500' : 'text-primary'
                }`}
                animate={phase === 'running' ? { opacity: [1, 0.85, 1] } : {}}
                transition={{ duration: 1, repeat: Infinity }}
              >
                {formatTime(elapsed)}
              </motion.div>
              <p className="text-xs text-muted-foreground mt-1">
                {formatMinutes(elapsed)} minute{formatMinutes(elapsed) !== 1 ? 's' : ''} elapsed
              </p>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-3">
              {phase === 'running' && (
                <>
                  <Button variant="outline" onClick={handlePause} iconName="Pause" iconPosition="left" size="sm">
                    Pause
                  </Button>
                  <Button variant="destructive" onClick={handleStop} iconName="Square" iconPosition="left" size="sm">
                    Stop
                  </Button>
                </>
              )}
              {phase === 'paused' && (
                <>
                  <Button variant="default" onClick={handleResume} iconName="Play" iconPosition="left" size="sm">
                    Resume
                  </Button>
                  <Button variant="destructive" onClick={handleStop} iconName="Square" iconPosition="left" size="sm">
                    Stop
                  </Button>
                </>
              )}
              {phase === 'done' && !saveSuccess && (
                <Button
                  variant="default"
                  onClick={handleSaveSession}
                  loading={saving}
                  iconName="Save"
                  iconPosition="left"
                  className="w-full"
                >
                  Save Session & Auto-fill Log
                </Button>
              )}
            </div>

            {/* Save success */}
            {saveSuccess && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-3 bg-green-500/10 border border-green-500/30 rounded-lg p-3"
              >
                <Icon name="CheckCircle" size={18} className="text-green-500 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">Session saved!</p>
                  <p className="text-xs text-muted-foreground">Activity log & workout record updated. Form pre-filled below.</p>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default WorkoutStopwatch;
